import { useEffect, useMemo, useRef, useState } from 'react';
import QRCode from 'qrcode';
import { RotateCw, Smile } from 'lucide-react';
import CloseButton from '@shared/components/CloseButton';
import { useLanguage } from '@shared/contexts/LanguageContext';
import { getPhysicalGiftExchangeCode, getPhysicalGiftRecordResult } from '../api/profileService';

const POLL_INTERVAL_MS = 5000;

const formatRemainingUntilExpiry = (expiryMs, nowMs) => {
  if (expiryMs == null || !Number.isFinite(expiryMs)) return '0:00';
  const msLeft = Math.max(0, expiryMs - nowMs);
  const totalSec = Math.floor(msLeft / 1000);
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  if (h > 0) return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  return `${String(m).padStart(1)}:${String(s).padStart(2, '0')}`;
};

export default function PhysicalGiftExchangeModal({ isOpen, onClose, ruleId, ruleTitle, onExchanged }) {
  const { t } = useLanguage();
  const [qrDataUrl, setQrDataUrl] = useState(null);
  const [error, setError] = useState(false);
  const [result, setResult] = useState(null);
  const [expiryMs, setExpiryMs] = useState(null);
  const [expiryTick, setExpiryTick] = useState(0);
  const notifiedRef = useRef(false);

  // 開啟時先拿兌換碼（rule_code + user_code + expiry），再組成 QR code 內容
  const fetchExchangeCode = () => {
    if (!ruleId) return;
    let cancelled = false;
    setError(false);
    setQrDataUrl(null);
    getPhysicalGiftExchangeCode({ rule_id: ruleId })
      .then((res) => {
        if (cancelled) return;
        const expiryValue = Date.parse(String(res?.expiry ?? ''));
        setExpiryMs(Number.isFinite(expiryValue) ? expiryValue : null);
        const qrPayload = JSON.stringify({ rule_code: res?.rule_code, user_code: res?.user_code });
        return QRCode.toDataURL(qrPayload, { width: 280, margin: 2, errorCorrectionLevel: 'M' });
      })
      .then((url) => {
        if (!cancelled && url) setQrDataUrl(url);
      })
      .catch((err) => {
        console.error('Failed to get physical gift exchange code:', err);
        if (!cancelled) setError(true);
      });

    return () => {
      cancelled = true;
    };
  };

  useEffect(() => {
    if (!isOpen || !ruleId) {
      setQrDataUrl(null);
      setResult(null);
      setError(false);
      setExpiryMs(null);
      notifiedRef.current = false;
      return undefined;
    }
    return fetchExchangeCode();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, ruleId]);

  // 每秒重繪一次，讓到期倒數持續更新
  useEffect(() => {
    if (!isOpen || !qrDataUrl || result || expiryMs == null || !Number.isFinite(expiryMs)) return undefined;
    if (Date.now() >= expiryMs) {
      setExpiryTick((n) => n + 1);
      return undefined;
    }
    const intervalId = setInterval(() => {
      setExpiryTick((n) => n + 1);
      if (Date.now() >= expiryMs) clearInterval(intervalId);
    }, 1000);
    return () => clearInterval(intervalId);
  }, [isOpen, qrDataUrl, result, expiryMs]);

  const expiryCaption = useMemo(() => {
    if (!qrDataUrl || result || expiryMs == null || !Number.isFinite(expiryMs)) return null;
    const now = Date.now();
    return { countdownStr: formatRemainingUntilExpiry(expiryMs, now), expired: expiryMs <= now };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [qrDataUrl, result, expiryMs, expiryTick]);

  // 顯示 QR code 期間，長輪詢兌換結果，直到 admin 掃描完成或 QR 到期
  useEffect(() => {
    if (!isOpen || !ruleId || !qrDataUrl || result) return undefined;
    if (expiryMs != null && Date.now() >= expiryMs) return undefined;

    let cancelled = false;
    const poll = async () => {
      try {
        const res = await getPhysicalGiftRecordResult({ rule_id: ruleId });
        if (cancelled) return;
        if (res?.is_exchange) {
          setResult({ message: res?.scan_msg || t('profile.physicalGiftExchangeSuccess'), code: res?.record_code });
          if (!notifiedRef.current) {
            notifiedRef.current = true;
            onExchanged && onExchanged(ruleId);
          }
        }
      } catch (err) {
        console.error('Failed to poll physical gift exchange result:', err);
      }
    };
    void poll();
    const intervalId = setInterval(poll, POLL_INTERVAL_MS);

    return () => {
      cancelled = true;
      clearInterval(intervalId);
    };
  }, [isOpen, ruleId, qrDataUrl, result, expiryMs, onExchanged, t]);

  if (!isOpen) return null;

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4"
      style={{ background: 'rgba(0,0,0,0.45)', backdropFilter: 'blur(2px)' }}
      onClick={handleBackdropClick}
    >
      <div
        className="w-96 p-6 relative bg-white rounded-[20px] shadow-[0px_11px_23px_0px_rgba(0,0,0,0.10)] shadow-[0px_42px_42px_0px_rgba(0,0,0,0.09)] shadow-[0px_95px_57px_0px_rgba(0,0,0,0.05)] flex flex-col items-center gap-4"
        onClick={(e) => e.stopPropagation()}
      >
        <CloseButton onClick={onClose} />

        <div className="text-center text-[#bc313d] text-2xl font-bold font-['Noto_Serif_TC'] leading-8 pr-6 pt-2">
          {ruleTitle}
        </div>

        <div className="w-[280px] min-h-[280px] flex items-center justify-center">
          {result ? (
            <div className="flex flex-col items-center gap-2">
              <Smile size={96} color="#00A86B" />
              <div className="text-2xl font-bold text-[#171717]">{result.message}</div>
              {result.code && (
                <div className="text-2xl font-bold text-[#171717]">{t('profile.physicalGiftRecordCode').replace('{{code}}', result.code)}</div>
              )}
            </div>
          ) : error ? (
            <div className="text-[#BC313D] font-medium">{t('profile.physicalGiftQrFailed')}</div>
          ) : qrDataUrl ? (
            expiryCaption?.expired ? (
              <div className="flex flex-col items-center gap-3">
                <div className="text-[#BC313D] font-bold">{t('profile.physicalGiftQrExpired')}</div>
                <button
                  type="button"
                  onClick={fetchExchangeCode}
                  className="flex items-center gap-1.5 rounded-full bg-[#111] px-4 py-2 text-sm font-bold text-white"
                >
                  <RotateCw size={16} />
                  {t('profile.physicalGiftRegenerateQr')}
                </button>
              </div>
            ) : (
              <img src={qrDataUrl} alt={t('profile.physicalGiftQrAlt')} width={280} height={280} style={{ display: 'block' }} />
            )
          ) : (
            <div className="text-[#6E625B] font-medium">{t('profile.loading')}</div>
          )}
        </div>

        {expiryCaption && !expiryCaption.expired && (
          <div className="text-center text-[#171717] text-sm font-bold leading-relaxed">
            {t('profile.physicalGiftValidTime').replace('{{time}}', expiryCaption.countdownStr)}
          </div>
        )}

        {!result && !error && (
          <div className="text-center text-[#6E625B] text-sm leading-relaxed">
            {t('profile.physicalGiftQrDesc')}
          </div>
        )}
      </div>
    </div>
  );
}
